import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../services/api';
import { USER_TYPES } from '../../constants/userTypes';
import { useTranslation } from 'react-i18next';

/**
 * TokenLogin Component
 * 
 * Allows users (specially mobile developers) to log in directly via a token in the URL.
 * Example: /auth/token-login?token=ACCESS_TOKEN&refresh_token=REFRESH_TOKEN
 */
const TokenLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { oauthLogin } = useAuth();
    const { t } = useTranslation();
    const [error, setError] = useState(null);
    const [status, setStatus] = useState('initializing');

    useEffect(() => {
        const handleTokenLogin = async () => {
            let token = searchParams.get('token');
            const refreshToken = searchParams.get('refresh_token');
            let redirectPath = searchParams.get('redirect');

            // Fallback for cases where the user might have omitted '?token=' or has a malformed URL
            if (!token) {
                const fullSearch = window.location.search || window.location.href.split('?')[1] || '';
                if (fullSearch.includes('token-login=')) {
                    // Handle the user's specific case: /auth/token-login=TOKEN&...
                    const match = window.location.href.match(/token-login=([^&]+)/);
                    if (match) token = match[1];
                } else if (fullSearch && !fullSearch.includes('=')) {
                    // Handle case where only the token is passed: /auth/token-login?TOKEN
                    token = fullSearch.replace('?', '');
                }
            }

            if (!token) {
                setStatus('error');
                setError(t('login.errors.tokenMissing') || 'Token is missing from URL');
                setTimeout(() => navigate('/auth/login'), 2000);
                return;
            }

            try {
                setStatus('verifying');

                // 1. Temporarily store token to fetch profile
                localStorage.setItem('access_token', token);
                if (refreshToken) {
                    localStorage.setItem('refresh_token', refreshToken);
                }

                // 2. Fetch user profile
                // Explicitly pass the token to ensure it's used even if interceptors haven't fully initialized
                const response = await authAPI.getProfile({
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const userData = response.data.data || response.data;

                if (!userData) {
                    throw new Error('Failed to fetch user data');
                }

                // 3. Setup persistence
                localStorage.setItem('user_data', JSON.stringify(userData));
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('auth_loading', 'false');
                localStorage.setItem('last_login', new Date().toISOString());

                // 4. Update Auth Context
                await oauthLogin(userData, token);

                setStatus('success');

                // 5. Redirection logic
                if (redirectPath) {
                    // Ensure redirect path is absolute
                    const target = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`;
                    navigate(target, { replace: true });
                    return;
                }

                // Default redirection based on user type
                const userType = userData.user_type || userData.userType || userData.type;

                if (userType === USER_TYPES.ADMIN) {
                    navigate('/admin/dashboard', { replace: true });
                } else if (userType === USER_TYPES.CLINIC_MODERATOR) {
                    navigate('/medical/clinic/appointment-management/appointments', { replace: true });
                } else if (userType === USER_TYPES.DEV_ADMIN) {
                    navigate('/dev-admin', { replace: true });
                } else {
                    const intendedRoute = localStorage.getItem('intended_route') || '/social';
                    localStorage.removeItem('intended_route');
                    navigate(intendedRoute, { replace: true });
                }

            } catch (err) {
                console.error('Token login failed:', err);
                setStatus('error');
                setError(t('login.errors.tokenInvalid') || 'Authentication failed. Invalid or expired token.');

                // Clear the failing tokens
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');

                setTimeout(() => navigate('/auth/login'), 3000);
            }
        };

        handleTokenLogin();
    }, [searchParams, navigate, oauthLogin, t]);

    return (
        <div style={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#FAFAFA',
            fontFamily: '"Red Hat Display", sans-serif'
        }}>
            <div style={{
                padding: '40px',
                background: '#FFFFFF',
                borderRadius: '24px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                textAlign: 'center',
                maxWidth: '400px',
                width: '90%'
            }}>
                <div style={{ marginBottom: '24px' }}>
                    <img src="/assets/icons/logo.svg" alt="Sulhafa" style={{ width: '80px', height: 'auto' }} />
                </div>

                {status === 'initializing' && (
                    <p style={{ color: '#6B7280' }}>Initializing secure login...</p>
                )}

                {status === 'verifying' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            border: '4px solid #E5E7EB',
                            borderTop: '4px solid #8548A8',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginBottom: '16px'
                        }}></div>
                        <p style={{ color: '#1F2937', fontWeight: 600 }}>Authenticating your session...</p>
                        <p style={{ color: '#6B7280', fontSize: '14px', marginTop: '8px' }}>Please wait a moment</p>
                    </div>
                )}

                {status === 'success' && (
                    <div>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            background: '#10B981',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <p style={{ color: '#10B981', fontWeight: 600 }}>Login Successful!</p>
                        <p style={{ color: '#6B7280', fontSize: '14px', marginTop: '8px' }}>Redirecting to your dashboard...</p>
                    </div>
                )}

                {status === 'error' && (
                    <div>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            background: '#EF4444',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 5L5 15M5 5L15 15" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <p style={{ color: '#EF4444', fontWeight: 600 }}>Login Failed</p>
                        <p style={{ color: '#6B7280', fontSize: '14px', marginTop: '8px' }}>{error}</p>
                        <p style={{ color: '#9CA3AF', fontSize: '12px', marginTop: '16px' }}>Redirecting to login page...</p>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default TokenLogin;
